import type { SceneNode } from "../scene/model";
import { smoothPenContour } from "./pen-smoothing-curve";
import { vectorPath } from "./vector-path";

/** Old pen layers had only a name; new ones retain their origin after renaming. */
export function supportsPenSmoothing(node: SceneNode) {
  return (
    node.kind === "path" &&
    !node.geometry.svgPath &&
    !!vectorPath(node) &&
    !vectorPath(node).arrow &&
    (!!vectorPath(node).pen ||
      /^Ручка(?:$|[ (])/iu.test(String(node.layer.name)))
  );
}

export function penSmoothing(node: SceneNode) {
  return vectorPath(node).pen?.amount ?? 0;
}

/** Capture the shipped contour, never the raw unsmoothed pointer samples. */
export function rememberPen(node: SceneNode): SceneNode {
  const vector = vectorPath(node);
  return {
    ...node,
    geometry: {
      ...node.geometry,
      vector: {
        ...vector,
        pen: {
          amount: 0,
          source: vector.anchors,
          width: node.geometry.width!,
          height: node.geometry.height!,
        },
      },
    },
  };
}

/** Pure, reversible geometry edit; the affine frame, appearance and roles are untouched. */
export function changePenSmoothing(
  nodes: SceneNode[],
  requested: number,
): SceneNode[] {
  if (!Number.isFinite(requested)) throw new Error("Некорректное сглаживание.");
  const amount = Math.max(0, Math.min(100, Math.round(requested)));
  const contours = new Map<string, ReturnType<typeof smoothPenContour>>();
  return nodes.filter(supportsPenSmoothing).map((node) => {
    if (penSmoothing(node) === amount) return node;
    const vector = vectorPath(node),
      baseline = vector.pen ?? vectorPath(rememberPen(node)).pen!,
      { source, width, height } = baseline;
    const key = JSON.stringify([source, width, height, vector.closed]);
    let anchors = contours.get(key);
    if (!anchors) {
      anchors = smoothPenContour(source, width, height, vector.closed, amount);
      contours.set(key, anchors);
    }
    return {
      ...node,
      geometry: {
        ...node.geometry,
        vector: {
          ...vector,
          anchors,
          pen: { ...baseline, amount },
        },
      },
    };
  });
}
